const NODE_W = 196;
const NODE_H = 82;
const H_GAP = 248;
const V_GAP = 120;
const PADDING = 56;
const LOOP_LANE = 80;

function buildPrimaryPath(states, primaryNextByCode) {
  const start = states.find((s) => s.is_start) || states[0];
  if (!start) return [];
  const path = [];
  const seen = new Set();
  let current = start.code;
  while (current && !seen.has(current)) {
    seen.add(current);
    path.push(current);
    const next = primaryNextByCode[current];
    if (!next || seen.has(next)) break;
    current = next;
  }
  return path;
}

function buildEdges(states, matrix, primaryNextByCode) {
  const byCode = new Map(states.map((s) => [s.code, s]));
  const edges = [];
  for (const from of states) {
    for (const toCode of matrix[from.code] || []) {
      const to = byCode.get(toCode);
      if (!to) continue;
      edges.push({
        id: `${from.code}->${toCode}`,
        from: from.code,
        to: toCode,
        isPrimary: primaryNextByCode[from.code] === toCode,
      });
    }
  }
  return edges;
}

function layoutFlowGraph(states, matrix, primaryNextByCode) {
  const primaryPath = buildPrimaryPath(states, primaryNextByCode);
  const positions = {};
  const placed = new Set();
  const spineY = PADDING + LOOP_LANE;

  primaryPath.forEach((code, index) => {
    positions[code] = {
      x: PADDING + index * H_GAP,
      y: spineY,
    };
    placed.add(code);
  });

  const visitOrder = [...primaryPath];

  while (visitOrder.length) {
    const from = visitOrder.shift();
    const fromPos = positions[from];
    if (!fromPos) continue;

    const newTargets = (matrix[from] || [])
      .filter((code) => code !== from && !placed.has(code));

    newTargets.forEach((code, index) => {
      const spread = newTargets.length > 1
        ? (index - (newTargets.length - 1) / 2) * (NODE_W + 36)
        : 0;
      positions[code] = {
        x: fromPos.x + spread,
        y: fromPos.y + V_GAP,
      };
      placed.add(code);
      visitOrder.push(code);
    });
  }

  for (const state of states) {
    if (placed.has(state.code)) continue;
    const parents = states
      .filter((s) => (matrix[s.code] || []).includes(state.code))
      .map((s) => s.code)
      .filter((code) => positions[code]);
    const anchor = parents[0] || primaryPath[0];
    const anchorPos = positions[anchor];
    if (!anchorPos) continue;
    positions[state.code] = {
      x: anchorPos.x,
      y: anchorPos.y + V_GAP * 2,
    };
    placed.add(state.code);
  }

  const bounds = computeBounds(positions);
  const normalized = {};
  for (const [code, pos] of Object.entries(positions)) {
    normalized[code] = {
      x: pos.x - bounds.minX + PADDING,
      y: pos.y - bounds.minY + PADDING,
    };
  }

  const normBounds = computeBounds(normalized);

  return {
    positions: normalized,
    canvasWidth: Math.max(640, normBounds.width + PADDING * 2),
    canvasHeight: Math.max(420, normBounds.height + PADDING * 2 + LOOP_LANE),
    primaryPath,
    bounds: normBounds,
    nodeWidth: NODE_W,
    nodeHeight: NODE_H,
  };
}

function computeBounds(positions) {
  const points = Object.values(positions);
  if (!points.length) {
    return { minX: 0, minY: 0, maxX: NODE_W, maxY: NODE_H, width: NODE_W, height: NODE_H };
  }
  const minX = Math.min(...points.map((p) => p.x));
  const minY = Math.min(...points.map((p) => p.y));
  const maxX = Math.max(...points.map((p) => p.x)) + NODE_W;
  const maxY = Math.max(...points.map((p) => p.y)) + NODE_H;
  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY,
  };
}

export function getDiagramContentBounds(positions) {
  return computeBounds(positions);
}

export function buildWorkflowDiagramModel(states, matrix, primaryNextByCode) {
  const layout = layoutFlowGraph(states, matrix, primaryNextByCode);
  const edges = buildEdges(states, matrix, primaryNextByCode);
  return { ...layout, edges };
}

export function classifyEdge(from, to) {
  const fx = from.x + NODE_W / 2;
  const tx = to.x + NODE_W / 2;
  const dx = tx - fx;
  const dy = (to.y + NODE_H / 2) - (from.y + NODE_H / 2);

  if (Math.abs(dy) < 40 && dx > 48) return 'forward';
  if (dx < -36 || (dy < -28 && Math.abs(dx) > 24)) return 'loop';
  if (dy > 28) return 'branch';
  if (Math.abs(dx) < 24 && Math.abs(dy) < 24) return 'self';
  if (dy < -28) return 'loop';
  return 'branch';
}

export function buildEdgePath(from, to, kind) {
  const fx = from.x + NODE_W / 2;
  const fy = from.y + NODE_H / 2;
  const tx = to.x + NODE_W / 2;
  const ty = to.y + NODE_H / 2;

  if (kind === 'forward') {
    const x1 = from.x + NODE_W;
    const y1 = fy;
    const x2 = to.x;
    const y2 = ty;
    const mx = (x1 + x2) / 2;
    return `M ${x1} ${y1} C ${mx} ${y1}, ${mx} ${y2}, ${x2} ${y2}`;
  }

  if (kind === 'loop') {
    const loopY = Math.max(from.y + NODE_H, to.y + NODE_H) + LOOP_LANE;
    const x1 = fx;
    const y1 = from.y + NODE_H;
    const x2 = tx;
    const y2 = to.y;
    return `M ${x1} ${y1} C ${x1} ${loopY}, ${x2} ${loopY}, ${x2} ${y2}`;
  }

  if (kind === 'self') {
    const loopY = from.y + NODE_H + 40;
    return `M ${fx} ${from.y + NODE_H} C ${fx + 56} ${loopY}, ${fx - 56} ${loopY}, ${fx} ${from.y}`;
  }

  const x1 = fx;
  const y1 = from.y + NODE_H;
  const x2 = tx;
  const y2 = to.y;
  const bend = Math.max(40, Math.abs(y2 - y1) * 0.45);
  return `M ${x1} ${y1} C ${x1} ${y1 + bend}, ${x2} ${y2 - bend}, ${x2} ${y2}`;
}

export { NODE_W, NODE_H };